import { Button, TextInput } from 'components/core';
import { default as NextImage } from 'next/legacy/image';
import { FC, useRef, useState } from 'react';
import { useAssignTemplateToOrder } from 'reactQuery/mutations/customClamshell/useAssignTemplateToOrder';
import { ClamshellCustomizationOptionsProps, Styled } from '.';
import { validateWebsiteUrl } from './helpers';
import { useI18n } from 'contexts/i18n';

const ClamshellCustomizationOptions: FC<ClamshellCustomizationOptionsProps> = ({
  selectedLogo,
  setSelectedLogo,
  websiteUrl,
  setWebsiteUrl,
  qrCodeText,
  setQRCodeText,
  designName,
  setDesignName,
  onLogoChange,
  isQrcodeSameAsWeburl,
  setQrcodeSameAsWeburl,
  onSaveDesign,
  templateMode,
  setTemplateMode,
  selectedTemplate,
  shouldEnableSaveAction,
  isLogoError,
}) => {
  const assignTemplateToOrderMutation = useAssignTemplateToOrder();
  const [isTCAccepted, setIsTCAccpeted] = useState(false);
  const hiddenFileInput = useRef<any>(null);
  const i18n = useI18n();
  const handleClick = () => {
    hiddenFileInput &&
      hiddenFileInput.current &&
      //@ts-ignore
      hiddenFileInput.current.click();
  };

  const selectTempleForOrder = async () => {
    if (templateMode === 'create') {
      const template = await onSaveDesign();

      if (template) {
        assignTemplateToOrderMutation.mutate({
          templateCode: template.data.code,
        });
      }
    }
    if (selectedTemplate && templateMode === 'select') {
      assignTemplateToOrderMutation.mutate({
        templateCode: selectedTemplate.code,
      });
    }
  };

  const shouldEnableAddToOrder = () => {
    return shouldEnableSaveAction() && isTCAccepted;
  };
  return (
    <>
      <Styled.PersonalizeOptionsSection>
        <Styled.Title>{i18n['Personalize Your Case']}</Styled.Title>
        <Styled.LogoUploadSection
          templateMode={templateMode}
          onClick={handleClick}
        >
          {/* <Styled.LogoUploadTitle>
        Drag & drop files to Upload
      </Styled.LogoUploadTitle> */}

          <Styled.LogoUploadIconWrpper>
            <NextImage
              src="/images/icons/upload.svg"
              alt="Invisalign"
              height={48}
              width={48}
            />
            <input
              type="file"
              disabled={templateMode === 'select'}
              ref={hiddenFileInput}
              onChange={onLogoChange}
              onClick={(event: any) => {
                event.target.value = null;
              }}
              style={{ display: 'none' }}
              accept="image/png,image/jpeg"
            />
            <div>{i18n['Upload Logo']}</div>
          </Styled.LogoUploadIconWrpper>
          <Styled.ListWithTitle>
            <Styled.ListTitle>{i18n['Logo Requirements']}:</Styled.ListTitle>
            <Styled.List>
              <Styled.ListItem>{i18n['Upload .png or .jpg']}</Styled.ListItem>
              <Styled.ListItem>
                {i18n['We encourage to use art that will contrast against the dark background.']}
              </Styled.ListItem>
              <Styled.ListItem>
                {i18n['The logo will be reviewed internally']}
              </Styled.ListItem>
              <Styled.ListItem>
                {i18n['The minimum resolution of the image must be 570px * 200px.']}
                {i18n['Preferred size is 1140px * 400px.']}
              </Styled.ListItem>
            </Styled.List>
          </Styled.ListWithTitle>
          {isLogoError && (
            <Styled.LogoErrorMessage>
              {i18n['Your uploaded image does not meet the logo requirements.']}
            </Styled.LogoErrorMessage>
          )}
        </Styled.LogoUploadSection>

        <Styled.QRCodeSection templateMode={templateMode}>
          <TextInput
            value={websiteUrl}
            disabled={templateMode === 'select'}
            width="90%"
            onChange={e => {
              setWebsiteUrl(e.target.value);
              if (isQrcodeSameAsWeburl) {
                setQRCodeText(e.target.value);
              }
            }}
            isError={!validateWebsiteUrl(websiteUrl)}
            size="sm"
            variant={'grey'}
            placeholder={i18n['Enter your Web Url']}
            inputIcon="/images/icons/web.svg"
            maxLength={30}
          />
          <br />

          <Styled.CheckboxContainer>
            <input
              type="checkbox"
              checked={isQrcodeSameAsWeburl}
              onChange={e => {
                setQrcodeSameAsWeburl(e.target.checked);
                if (e.target.checked) {
                  setQRCodeText(websiteUrl);
                }
              }}
              disabled={templateMode === 'select'}
            />
            <div>{i18n['Use same as Web url']}</div>
          </Styled.CheckboxContainer>
          <TextInput
            value={qrCodeText}
            disabled={isQrcodeSameAsWeburl || templateMode === 'select'}
            width="90%"
            onChange={e => {
              setQRCodeText(e.target.value);
            }}
            isError={false}
            size="sm"
            placeholder={i18n['Enter your QR Code text']}
            optionHelperText={i18n['Your QR Code will be generated automatically']}
            inputIcon="/images/icons/qrcode.svg"
          />
          <br />
          <TextInput
            value={designName}
            width="90%"
            onChange={e => {
              setDesignName(e.target.value);
            }}
            isError={designName.length === 0}
            size="sm"
            placeholder={i18n['Enter your design name']}
            disabled={templateMode === 'select'}
          />
        </Styled.QRCodeSection>
        {(templateMode === 'select' || templateMode === 'create') && (
          <Styled.TCWrapper>
            <input
              type="checkbox"
              checked={isTCAccepted}
              onChange={e => {
                setIsTCAccpeted(e.target.checked);
              }}
              id="checkbox_id"
            />
            <label htmlFor="checkbox_id">
              {i18n['Confirm you agree with design layout and items included during custom design process. This needs to be confirmed prior adding to an order.']}
            </label>
          </Styled.TCWrapper>
        )}
        <Styled.ActionButtonContainer templateMode={templateMode}>
          {(templateMode === 'select' ||
            templateMode === 'edit' ||
            templateMode === 'clone') && (
            <Button
              size="md"
              variant="secondary"
              onClick={() => {
                setTemplateMode('list');
              }}
            >
              {i18n['Back']}
            </Button>
          )}
          {templateMode !== 'select' && (
            <>
              <Button
                size="md"
                onClick={() => {
                  setSelectedLogo(undefined);
                  setWebsiteUrl('');
                  setQRCodeText('');
                  setDesignName('');
                }}
                variant="secondary"
              >
                {i18n['Reset']}
              </Button>
              <Button
                size="md"
                variant="primary"
                onClick={() => {
                  onSaveDesign();
                }}
                disabled={!shouldEnableSaveAction()}
              >
                {templateMode === 'edit'
                  ? i18n['Update template']
                  : i18n['Save as template']}
              </Button>
            </>
          )}

          {(templateMode === 'create' || templateMode === 'select') && (
            <Button
              size="md"
              onClick={selectTempleForOrder}
              disabled={!shouldEnableAddToOrder()}
              loading={assignTemplateToOrderMutation.isLoading}
            >
              {i18n['Add to Order']}
            </Button>
          )}
        </Styled.ActionButtonContainer>
      </Styled.PersonalizeOptionsSection>
    </>
  );
};

export default ClamshellCustomizationOptions;
